import { createFileRoute, Link } from '@tanstack/react-router'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { QueryGuard } from '@/components/query-guard'
import { AdminCreditsSection } from '@/features/admin/components/admin-credits-section'
import { StartupView } from '@/features/startup/components/startup-view'
import {
  useAdminAddStartupCreditsMutation,
  useAdminStartupByIdQuery,
  useAdminStartupCreditsQuery,
} from '@/hooks/api'

export const Route = createFileRoute('/_protected/admin/startups/$id')({
  component: AdminStartupDetailPage,
})

const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'credits', label: 'Credits' },
] as const

type Tab = (typeof tabs)[number]['key']

function AdminStartupDetailPage() {
  const { id } = Route.useParams()
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const startupQuery = useAdminStartupByIdQuery(id)
  const creditsQuery = useAdminStartupCreditsQuery(id)
  const addCreditsMutation = useAdminAddStartupCreditsMutation()

  const handleAddCredits = async (amount: number, note?: string) => {
    await addCreditsMutation.mutateAsync({
      path: { id },
      body: { amount, note },
    })
  }

  return (
    <QueryGuard query={startupQuery} errorMessage="Failed to load startup">
      {(startup) => (
        <>
          <nav className="border-b border-border">
            <div className="flex items-center gap-2 py-3">
              <Link
                to="/admin/startups"
                className="flex items-center gap-1 text-sm font-medium text-[#f97316] hover:underline"
              >
                <ArrowLeft className="size-4" />
                Startups
              </Link>
              <ChevronRight className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">{startup.name}</span>
            </div>
            <div className="flex gap-1 -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                    activeTab === tab.key
                      ? 'border-[#f97316] text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </nav>
          <div className="py-6">
            {activeTab === 'overview' && <StartupView startup={startup} canEdit={false} />}
            {activeTab === 'credits' && (
              <AdminCreditsSection
                balance={creditsQuery.data?.data.balance}
                transactions={creditsQuery.data?.data.transactions}
                isLoading={creditsQuery.isLoading}
                onAddCredits={handleAddCredits}
                isAdding={addCreditsMutation.isPending}
              />
            )}
          </div>
        </>
      )}
    </QueryGuard>
  )
}
